/**
 * InvoicesCard — Customer 360 Sprint (Day 4)
 *
 * Inline card on CustomerDetailModal's Info tab. Rolls up open invoices
 * for this customer — outstanding total, count, bad-debtor flag count,
 * most-recent billed-at — with a deep link to the Billing page.
 *
 * "Open invoice" definition (mirrored in aggregate-customer.service.ts):
 *   - issued (billingStatus === 'billed' OR 'bad_debtor')
 *   - unpaid (billingStatus !== 'paid')
 *   - not voided (proxy: !deal.deleted — no explicit void status exists)
 *
 * Loading vs empty discipline: skeleton while parent is loading; only show
 * "No open invoices" once loading has finished and the count is zero.
 */

import { getLocaleSync } from '@/utils/locale';
import { motion } from 'framer-motion';
import { Receipt, AlertTriangle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { InvoiceSummary } from '@/services/aggregate-customer.service';
import { formatCurrency } from '@/utils/pricing';

interface InvoicesCardProps {
  summary: InvoiceSummary;
  loading: boolean;
  /** Deep-link target: clicking the card navigates to billing filtered by customer. */
  customerId: string | null;
}

export function InvoicesCard({ summary, loading, customerId }: InvoicesCardProps) {
  const navigate = useNavigate();
  const handleOpenBilling = () => {
    if (!customerId) return;
    // Same-tab nav to /home/billing with a customer filter. The Billing page
    // accepts the param today via the standard ?customerId pattern.
    navigate(`/home/billing?customerId=${customerId}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-surface-card border border-theme rounded-xl p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Receipt className="w-4 h-4 text-brand-secondary" />
        <h3 className="text-sm font-bold text-theme-primary">Open invoices</h3>
        {!loading && summary.openCount > 0 && (
          <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-secondary/10 text-brand-secondary">
            {summary.openCount}
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2" aria-label="Loading invoice summary">
          <div className="h-8 rounded-lg bg-surface-hover animate-pulse w-2/3" />
          <div className="h-4 rounded-lg bg-surface-hover animate-pulse w-1/2" />
        </div>
      ) : summary.openCount === 0 ? (
        <p className="text-xs text-theme-tertiary">No open invoices.</p>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-xl font-bold text-theme-primary tabular-nums">
              {formatCurrency(summary.outstandingTotal, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] uppercase tracking-wide text-theme-tertiary">
              outstanding
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-theme-tertiary">
            <span>
              {summary.openCount} invoice{summary.openCount === 1 ? '' : 's'}
            </span>
            {summary.badDebtorCount > 0 && (
              <span className="inline-flex items-center gap-1 text-red-500/85">
                <AlertTriangle className="w-3 h-3" />
                {summary.badDebtorCount} bad debtor
              </span>
            )}
            {summary.mostRecentBilledAt && (
              <span>
                Last billed{' '}
                {new Date(summary.mostRecentBilledAt).toLocaleDateString(getLocaleSync(), {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}
          </div>

          {customerId && (
            <button
              onClick={handleOpenBilling}
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-secondary hover:underline"
            >
              Open in Billing
              <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </motion.div>
  );
}
